import type {
  BundleManuale,
  BundleManualeSubTheme,
  BundleManualeKeyPoints,
  BundleManualeBlock,
  BundleManualeChapter,
  BundleManualeCompendio,
} from '@arianna/shared'
import { normalizeBlocks } from '../markdown/index.js'

// ---------------------------------------------------------------------------
// ReaderRouteParent — chapter or compendio (TOC root node)
// ---------------------------------------------------------------------------

export type ReaderRouteParent =
  | { kind: 'chapter'; node: BundleManualeChapter }
  | { kind: 'compendio'; node: BundleManualeCompendio }

// ---------------------------------------------------------------------------
// ReaderRouteSection — indexable navigation unit
// ---------------------------------------------------------------------------

export type ReaderRouteSection = {
  // Identity of the active section in the viewer:
  id: string // sectionId: '1.1', '1.kp', 'compendio.1', etc.
  kind: 'subTheme' | 'keyPoints' // type of the selected node
  node: BundleManualeSubTheme | BundleManualeKeyPoints

  // Reference to the parent (chapter or compendio):
  parentKind: 'chapter' | 'compendio'
  parentId: string // chapter id ('1', '2', ...) or 'compendio'
  parentTitle: string // chapter.title or compendio.title
  parentNumericLabel: string | null // '1', '2', ... for chapter; null for compendio

  // Global reading order (0-based):
  flatIndex: number
}

// ---------------------------------------------------------------------------
// getIndexableSections
// ---------------------------------------------------------------------------

/**
 * Returns all indexable sections in global reading order:
 *   1. if manuale.compendio != null: all compendio.subThemes as kind='subTheme' parentKind='compendio'
 *   2. for each chapter: all chapter.subThemes (kind='subTheme' parentKind='chapter')
 *   3. after a chapter's subThemes: if chapter.keyPoints != null → emits 1 section kind='keyPoints'
 * flatIndex is 0-based and incremental.
 */
export function getIndexableSections(manuale: BundleManuale): ReaderRouteSection[] {
  const sections: ReaderRouteSection[] = []

  if (manuale.compendio != null) {
    const compendio = manuale.compendio
    for (const subTheme of compendio.subThemes) {
      sections.push({
        id: subTheme.id,
        kind: 'subTheme',
        node: subTheme,
        parentKind: 'compendio',
        parentId: 'compendio',
        parentTitle: compendio.title,
        parentNumericLabel: null,
        flatIndex: sections.length,
      })
    }
  }

  for (const chapter of manuale.chapters) {
    for (const subTheme of chapter.subThemes) {
      sections.push({
        id: subTheme.id,
        kind: 'subTheme',
        node: subTheme,
        parentKind: 'chapter',
        parentId: chapter.id,
        parentTitle: chapter.title,
        parentNumericLabel: chapter.id,
        flatIndex: sections.length,
      })
    }

    if (chapter.keyPoints != null) {
      sections.push({
        id: chapter.keyPoints.id,
        kind: 'keyPoints',
        node: chapter.keyPoints,
        parentKind: 'chapter',
        parentId: chapter.id,
        parentTitle: chapter.title,
        parentNumericLabel: chapter.id,
        flatIndex: sections.length,
      })
    }
  }

  return sections
}

// ---------------------------------------------------------------------------
// getRouteParents
// ---------------------------------------------------------------------------

/**
 * Returns all ReaderRouteParents ordered for the TOC (compendio before chapters
 * if present).
 */
export function getRouteParents(manuale: BundleManuale): ReaderRouteParent[] {
  const parents: ReaderRouteParent[] = []
  if (manuale.compendio != null) {
    parents.push({ kind: 'compendio', node: manuale.compendio })
  }
  for (const chapter of manuale.chapters) {
    parents.push({ kind: 'chapter', node: chapter })
  }
  return parents
}

// ---------------------------------------------------------------------------
// findSectionContainingSubParaId
// ---------------------------------------------------------------------------

/**
 * For quiz "Go to chapter": given a subParaId, find the ReaderRouteSection
 * that contains it.
 *
 * Rules:
 *   - if a keyPoints node has id === subParaId → returns its section
 *   - if a subParagraph node has id === subParaId → returns the subTheme containing it
 *   - otherwise null
 */
export function findSectionContainingSubParaId(
  manuale: BundleManuale,
  subParaId: string,
): ReaderRouteSection | null {
  const sections = getIndexableSections(manuale)
  for (const section of sections) {
    if (section.kind === 'keyPoints') {
      if (section.node.id === subParaId) return section
    } else {
      // kind === 'subTheme'
      const subTheme = section.node as BundleManualeSubTheme
      if (subTheme.subParagraphs.some((sp) => sp.id === subParaId)) return section
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// isSectionCompleted
// ---------------------------------------------------------------------------

/**
 * Kind-driven completion logic (fixes bug E):
 *   subTheme: requires subParagraphs.length > 0 AND all subParagraphs.id are in completedIds
 *   keyPoints: requires section.node.id is in completedIds
 */
export function isSectionCompleted(
  section: ReaderRouteSection,
  completedIds: ReadonlySet<string>,
): boolean {
  if (section.kind === 'keyPoints') {
    return completedIds.has(section.node.id)
  }
  // kind === 'subTheme'
  const subTheme = section.node as BundleManualeSubTheme
  return (
    subTheme.subParagraphs.length > 0 &&
    subTheme.subParagraphs.every((sp) => completedIds.has(sp.id))
  )
}

// ---------------------------------------------------------------------------
// isSectionForReview
// ---------------------------------------------------------------------------

/**
 * Kind-driven review logic:
 *   subTheme: section.node.subParagraphs.some(sp => reviewIds.has(sp.id))
 *   keyPoints: reviewIds.has(section.node.id)
 */
export function isSectionForReview(
  section: ReaderRouteSection,
  reviewIds: ReadonlySet<string>,
): boolean {
  if (section.kind === 'keyPoints') {
    return reviewIds.has(section.node.id)
  }
  const subTheme = section.node as BundleManualeSubTheme
  return subTheme.subParagraphs.some((sp) => reviewIds.has(sp.id))
}

// ---------------------------------------------------------------------------
// subParaIdsForCompletionToggle
// ---------------------------------------------------------------------------

/**
 * Returns the subParaIds to mark as completed/uncompleted when toggling a section:
 *   subTheme: all subParagraph.id values
 *   keyPoints: [section.node.id]
 */
export function subParaIdsForCompletionToggle(section: ReaderRouteSection): string[] {
  if (section.kind === 'keyPoints') {
    return [section.node.id]
  }
  const subTheme = section.node as BundleManualeSubTheme
  return subTheme.subParagraphs.map((sp) => sp.id)
}

// ---------------------------------------------------------------------------
// countableTotalForParent / countCompletedForParent
// ---------------------------------------------------------------------------

/**
 * Counters for parent root (TOC):
 *   chapter: subThemes.length + (keyPoints ? 1 : 0)
 *   compendio: subThemes.length
 */
export function countableTotalForParent(parent: ReaderRouteParent): number {
  if (parent.kind === 'compendio') {
    return parent.node.subThemes.length
  }
  return parent.node.subThemes.length + (parent.node.keyPoints != null ? 1 : 0)
}

export function countCompletedForParent(
  parent: ReaderRouteParent,
  completedIds: ReadonlySet<string>,
): number {
  // Build sections for this parent and count completed ones
  const sections: ReaderRouteSection[] = []

  if (parent.kind === 'compendio') {
    const compendio = parent.node
    for (const subTheme of compendio.subThemes) {
      sections.push({
        id: subTheme.id,
        kind: 'subTheme',
        node: subTheme,
        parentKind: 'compendio',
        parentId: 'compendio',
        parentTitle: compendio.title,
        parentNumericLabel: null,
        flatIndex: 0,
      })
    }
  } else {
    const chapter = parent.node
    for (const subTheme of chapter.subThemes) {
      sections.push({
        id: subTheme.id,
        kind: 'subTheme',
        node: subTheme,
        parentKind: 'chapter',
        parentId: chapter.id,
        parentTitle: chapter.title,
        parentNumericLabel: chapter.id,
        flatIndex: 0,
      })
    }
    if (chapter.keyPoints != null) {
      sections.push({
        id: chapter.keyPoints.id,
        kind: 'keyPoints',
        node: chapter.keyPoints,
        parentKind: 'chapter',
        parentId: chapter.id,
        parentTitle: chapter.title,
        parentNumericLabel: chapter.id,
        flatIndex: 0,
      })
    }
  }

  return sections.filter((s) => isSectionCompleted(s, completedIds)).length
}

// ---------------------------------------------------------------------------
// TOC label helpers
// ---------------------------------------------------------------------------

/**
 * Returns the display label for a TOC parent (chapter or compendio).
 *
 * iPad TocParent behaviour (source of truth):
 *   - compendio → "Compendio" (fixed string)
 *   - chapter   → chapter.title (full title; the numeric prefix is rendered
 *                 separately as a small badge via `parentPrefix = chapter.id`)
 *
 * For consumers that need a single concatenated string (e.g. web TOC where
 * prefix and title share one element) use `getTocParentFullLabel` instead.
 */
export function getTocParentLabel(parent: ReaderRouteParent): string {
  return parent.kind === 'compendio' ? 'Compendio' : parent.node.title
}

/**
 * Returns the numeric prefix shown in the small badge to the left of the
 * parent title in the iPad TOC:
 *   - compendio → '' (empty — no numeric prefix)
 *   - chapter   → chapter.id ('1', '2', ...)
 */
export function getTocParentPrefix(parent: ReaderRouteParent): string {
  return parent.kind === 'compendio' ? '' : parent.node.id
}

/**
 * Returns the full, single-string label for a TOC parent, matching the
 * visual result of the iPad TocParent (prefix + double-space + title):
 *   - compendio → "Compendio"
 *   - chapter   → "{id}  {title}"  (two spaces, like "1  Fondamenti Chimici")
 *
 * Use this in contexts where prefix and title cannot be rendered as separate
 * DOM/RN elements.
 */
export function getTocParentFullLabel(parent: ReaderRouteParent): string {
  if (parent.kind === 'compendio') return 'Compendio'
  return `${parent.node.id}  ${parent.node.title}`
}

/**
 * Returns the display id for a TOC section row, matching iPad TocSection
 * behaviour:
 *   - compendio child → '' (no numeric prefix)
 *   - chapter child   → section.id ('1.1', '1.kp', ...)
 */
export function getTocSectionDisplayId(section: ReaderRouteSection): string {
  return section.parentKind === 'compendio' ? '' : section.id
}

/**
 * Returns the full label for a TOC section, matching the visual result of the
 * iPad TocSection (displayId + double-space + title, or just title for
 * compendio children):
 *   - compendio child → section.node.title
 *   - chapter child   → "{section.id}  {section.node.title}"
 */
export function getTocSectionFullLabel(section: ReaderRouteSection): string {
  if (section.parentKind === 'compendio') return section.node.title
  return `${section.id}  ${section.node.title}`
}

// ---------------------------------------------------------------------------
// LoadedSection — content ready for rendering (pure, no I/O)
// ---------------------------------------------------------------------------

export type LoadedSectionPart =
  | { kind: 'subParagraph'; id: string; title: string; blocks: BundleManualeBlock[] }
  | { kind: 'keyPoints'; id: string; title: string; blocks: BundleManualeBlock[] }

export type LoadedSection = {
  section: ReaderRouteSection
  parts: LoadedSectionPart[]
}

// ---------------------------------------------------------------------------
// loadReaderSection — synchronous, no I/O
// ---------------------------------------------------------------------------

/**
 * Builds parts from the structured data of manuale.
 * For kind 'subTheme': parts = one per subParagraph of the subTheme.
 * For kind 'keyPoints': parts = one single part with kind 'keyPoints' from the node itself.
 */
export function loadReaderSection(
  manuale: BundleManuale,
  section: ReaderRouteSection,
): LoadedSection {
  if (section.kind === 'keyPoints') {
    const kp = section.node as BundleManualeKeyPoints
    return {
      section,
      parts: [
        { kind: 'keyPoints', id: kp.id, title: kp.title, blocks: normalizeBlocks(kp.blocks) },
      ],
    }
  }

  // kind === 'subTheme'
  const subTheme = section.node as BundleManualeSubTheme
  const parts: LoadedSectionPart[] = subTheme.subParagraphs.map((sp) => ({
    kind: 'subParagraph' as const,
    id: sp.id,
    title: sp.title,
    blocks: normalizeBlocks(sp.blocks),
  }))

  return { section, parts }
}
